/**
 * Cury (Diffuser) device action preparations
 */

import type {
	DeviceActionContext,
	PreparedDeviceAction,
} from 'src/components/Scenes/helpers/device_actions/types';
import type { DeviceAction } from 'src/types/DeviceActionUI';

/**
 * Resolves the device channel key for left/right slot aware actions.
 *
 * @param slot - Slot value coming from the device action (`left` or `right`).
 * @param leftKey - Key that should be used when the slot is `left`.
 * @param rightKey - Key that should be used when the slot is `right`.
 * @returns The resolved key appropriate for the provided slot.
 */
const resolveSlotKey = (slot: DeviceAction['slot'], leftKey: string, rightKey: string) =>
	slot === 'left' ? leftKey : rightKey;

enum CuryActionName {
	Intensity = 'cury_intensity',
	Ambient = 'cury_ambient',
	On = 'cury_on',
	Boost = 'cury_boost',
	Stop = 'cury_stop',
}

/**
 * List of action names supported by the Cury device.
 */
const CURY_SUPPORTED_ACTIONS = Object.values(CuryActionName) as readonly CuryActionName[];

/**
 * Prepares action payloads for the Cury diffuser device.
 *
 * @param context - Context containing shared helpers and the scene actions queue.
 * @returns Prepared action payload ready for transport, or `null` when the action is not supported.
 */
export const prepareCuryAction = (context: DeviceActionContext): PreparedDeviceAction | null => {
	const { hexToDecimal, actions } = context;
	const _action_el: DeviceAction = actions[0];
	const maybeActionName = _action_el?.name as CuryActionName | undefined;

	if (!maybeActionName || !CURY_SUPPORTED_ACTIONS.includes(maybeActionName)) {
		return null;
	}

	switch (maybeActionName) {
		case CuryActionName.Intensity: {
			const slotKey = resolveSlotKey(_action_el?.slot, 'cil', 'cir');

			return {
				_gui_purpose: 'action',
				r: `dev:${hexToDecimal}:${slotKey}`,
				set: _action_el.value,
			};
		}
		case CuryActionName.Ambient: {
			const ambientValue = _action_el.value as Record<string, any>;

			return {
				_gui_purpose: 'action',
				r: `dev:${hexToDecimal}:ca`,
				set: {
					_isdata: true,
					enable: ambientValue.enable,
					brightness: ambientValue.brightness,
					red: ambientValue.red,
					green: ambientValue.green,
					blue: ambientValue.blue,
				},
			};
		}
		case CuryActionName.On: {
			const slotKey = resolveSlotKey(_action_el?.slot, 'onl', 'onr');

			return {
				_gui_purpose: 'action',
				r: `dev:${hexToDecimal}:${slotKey}`,
				set: _action_el.value,
			};
		}
		case CuryActionName.Boost: {
			const slotKey = resolveSlotKey(_action_el?.slot, 'bl', 'br');

			return {
				_gui_purpose: 'action',
				r: `dev:${hexToDecimal}:${slotKey}`,
				set: _action_el.value,
			};
		}
		case CuryActionName.Stop: {
			const slotKey = resolveSlotKey(_action_el?.slot, 'bl', 'br');

			return {
				_gui_purpose: 'action',
				r: `dev:${hexToDecimal}:${slotKey}`,
				set: false, // Stop boost by setting to false
			};
		}
		default:
			return null;
	}
};
